import { Pencil, Plus } from 'lucide-react';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch, useSearchParams } from 'react-router';
import { ListToolbar } from '@/components/list/ListToolbar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePlaceContext } from '../place/PlaceLayout';
import { sectionPath } from '../place/place';
import { NamePanel, type NameSubmitResult } from '../ui/NamePanel';
import { PageHead } from '../ui/PageHead';
import { PhonePrimary } from '../ui/PhonePrimary';
import { TenantListPane } from './TenantListPane';
import { useTenantNames } from './use-tenant-names';
import { usePlatformHeaders, useTenants, type TenantRow } from './use-tenants';

export type { TenantRow };

/** What Tenant hands its card (`TenantSheet`): the rows as filtered now. */
export type TenantsContext = {
  readonly items: readonly TenantRow[];
  readonly loading: boolean;
  /** Opens the rename panel for a tenant; null when the caller may not rename. */
  readonly onRename: ((row: TenantRow) => void) | null;
  /** Tenant's own address: the list the card goes back to. */
  readonly listPath: string;
};

/**
 * Tenant (FR-AD-6, the third round of boards in decision 61), at the platform, for the global
 * administrator: every tenant with its organizations and its people, counted and never listed
 * (FR-GA-5). A new tenant has no organization; its row offers «Crea la prima organizzazione»,
 * which opens the tenant's Organizzazioni with the panel open. Tenant administrators are appointed
 * from the tenant itself.
 */
export function PlatformTenantsPage() {
  const { place, detail } = usePlaceContext();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const { state, more, reload } = useTenants(q);
  const headers = usePlatformHeaders();
  const canCreate = detail.can.browseTenants;
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<TenantRow | null>(null);
  const titleId = useId();

  const setQuery = useCallback(
    (value: string) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === '') {
            next.delete('q');
          } else {
            next.set('q', value);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const { create, rename } = useTenantNames(headers, reload);
  const items = state.items;
  const listPath = sectionPath(place, 'tenants');
  const sheetOpen = useMatch(`${listPath}/:tenantId`) !== null;
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const childContext: TenantsContext = {
    items,
    loading: state.status === 'loading',
    onRename: canCreate ? setRenaming : null,
    listPath,
  };

  return (
    <section
      aria-labelledby={titleId}
      className={cn('people-section flex flex-col gap-5', sheetOpen && 'people-section-with-sheet')}
    >
      <TenantsHead
        titleId={titleId}
        canCreate={canCreate}
        onCreate={() => setCreating(true)}
        q={q}
        setQuery={setQuery}
      />
      <div className={cn('people-layout', sheetOpen && 'people-layout-with-sheet')}>
        <div className="people-list-pane">
          <TenantListPane
            state={state}
            more={more}
            reload={reload}
            q={q}
            setQuery={setQuery}
            canCreate={canCreate}
            setRenaming={setRenaming}
            cardHrefFor={(row) => `${listPath}/${row.id}${query}`}
          />
        </div>
        <Outlet context={childContext} />
      </div>
      {canCreate ? <PhoneCreate onCreate={() => setCreating(true)} /> : null}
      <TenantNamePanels
        creating={creating}
        setCreating={setCreating}
        renaming={renaming}
        setRenaming={setRenaming}
        create={create}
        rename={rename}
      />
    </section>
  );
}

function PhoneCreate({ onCreate }: { readonly onCreate: () => void }) {
  const { t } = useTranslation('administration');
  return (
    <PhonePrimary>
      <Button onClick={onCreate}>
        <Plus />
        {t('tenants.new')}
      </Button>
    </PhonePrimary>
  );
}

function TenantsHead({
  titleId,
  canCreate,
  onCreate,
  q,
  setQuery,
}: {
  readonly titleId: string;
  readonly canCreate: boolean;
  readonly onCreate: () => void;
  readonly q: string;
  readonly setQuery: (value: string) => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-head">
      <PageHead
        titleId={titleId}
        title={t('tenants.title')}
        summary={t('tenants.summary')}
        actions={
          canCreate ? (
            <span className="page-primary">
              <Button onClick={() => onCreate()}>
                <Plus />
                {t('tenants.new')}
              </Button>
            </span>
          ) : undefined
        }
      />
      <TenantsToolbar q={q} setQuery={setQuery} />
    </div>
  );
}

function TenantNamePanels({
  creating,
  setCreating,
  renaming,
  setRenaming,
  create,
  rename,
}: {
  readonly creating: boolean;
  readonly setCreating: (open: boolean) => void;
  readonly renaming: TenantRow | null;
  readonly setRenaming: (row: TenantRow | null) => void;
  readonly create: (name: string) => Promise<NameSubmitResult>;
  readonly rename: (row: TenantRow, name: string) => Promise<NameSubmitResult>;
}) {
  const { t } = useTranslation('administration');
  return (
    <>
      <NamePanel
        open={creating}
        onOpenChange={setCreating}
        title={t('tenants.create.title')}
        help={t('tenants.create.help')}
        note={t('tenants.create.note')}
        submitLabel={t('tenants.create.submit')}
        submitIcon={<Plus />}
        onSubmit={create}
      />
      <NamePanel
        open={renaming !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenaming(null);
          }
        }}
        title={t('tenants.rename.title', { name: renaming?.name ?? '' })}
        initialName={renaming?.name ?? ''}
        help={t('tenants.rename.help')}
        submitLabel={t('tenants.rename.submit')}
        submitIcon={<Pencil />}
        onSubmit={(name) =>
          renaming === null ? Promise.resolve({ ok: true }) : rename(renaming, name)
        }
      />
    </>
  );
}

/** The shared filterable list's toolbar (decision 101): the tenants are searched by name. */
function TenantsToolbar({
  q,
  setQuery,
}: {
  readonly q: string;
  readonly setQuery: (value: string) => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <ListToolbar
      label={t('tenants.title')}
      search={{ label: t('tenants.searchLabel'), value: q, onWrite: setQuery }}
      fields={[]}
      active={{}}
      onFilter={() => undefined}
      onClear={() => setQuery('')}
    />
  );
}
